import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';

const INFLIGHT_KEY = 'sintra_payfast_inflight';
const PENDING_KEY = 'sintra_pending_booking';
const LOCAL_PENDING_KEY = 'sintra_payfast_pending';
/** Client-side abandon window (minutes). Server expire job uses the same default (3). */
const ABANDON_MINUTES = 3;

function readJson(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function resolveBasketId(inflight, pending, localPending) {
  return (
    inflight?.reservationNumber ||
    localPending?.reservationNumber ||
    pending?.bookingResponse?.reservationNumber ||
    pending?.bookingResponse?.ReservationNumber ||
    ''
  );
}

function clearPayfastStorage() {
  try {
    sessionStorage.removeItem(INFLIGHT_KEY);
    localStorage.removeItem(LOCAL_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

async function cancelOnServer(basketId, amount) {
  if (!basketId) return;
  const payload = {
    basketId,
    errCode: '901',
    errMessage: 'Payment not completed (abandoned checkout)',
    transactionAmount: amount ? String(amount) : '',
    transactionId: ''
  };

  try {
    const response = await fetch(`${API_BASE_URL}/Payfast/confirm-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });
    if (response.ok) return;
  } catch {
    /* fall through */
  }

  try {
    await fetch(`${API_BASE_URL}/CustomerReservation/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        reservationNumber: basketId,
        reason: 'PayFast payment not completed (abandoned checkout)'
      }),
      keepalive: true
    });
  } catch {
    /* server expire job will still clean up */
  }
}

function isThankYouPath(path) {
  const p = (path || '').toLowerCase();
  return p.startsWith('/thank-you') || p.includes('/thankyou');
}

/** Sintra has no /payment-failed route — SintraThankYou handles err_code failures. */
function goToPaymentFailed(navigate, basketId, amount, timedOut) {
  const params = new URLSearchParams();
  params.set('basket_id', basketId);
  params.set('err_code', '901');
  params.set(
    'err_msg',
    timedOut
      ? 'Payment timed out. Booking has been cancelled.'
      : 'Payment not completed. Booking has been cancelled.'
  );
  if (amount) params.set('transaction_amount', String(amount));
  navigate(`/thank-you?${params.toString()}`, { replace: true });
}

/**
 * Handles abandoned PayFast checkouts when the guest returns to the website.
 */
export default function usePayfastAbandonGuard() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const path = location.pathname || '';
    if (isThankYouPath(path)) {
      return undefined;
    }

    const inflight = readJson(sessionStorage, INFLIGHT_KEY);
    const pending = readJson(sessionStorage, PENDING_KEY);
    const localPending = readJson(localStorage, LOCAL_PENDING_KEY);

    if (inflight) {
      const basketId = resolveBasketId(inflight, pending, localPending);
      if (!basketId) {
        clearPayfastStorage();
        return undefined;
      }
      const amount = inflight?.amount || localPending?.amount || '';
      clearPayfastStorage();
      cancelOnServer(basketId, amount);
      goToPaymentFailed(navigate, basketId, amount, false);
      return undefined;
    }

    if (localPending?.reservationNumber && localPending?.createdAt) {
      const createdAt = Date.parse(localPending.createdAt);
      const ageMinutes = Number.isFinite(createdAt) ? (Date.now() - createdAt) / 60000 : 0;
      if (ageMinutes >= ABANDON_MINUTES) {
        const basketId = localPending.reservationNumber;
        const amount = localPending.amount || '';
        clearPayfastStorage();
        cancelOnServer(basketId, amount);
        goToPaymentFailed(navigate, basketId, amount, true);
      }
    }

    return undefined;
  }, [location.pathname, navigate]);
}

export function markPayfastInflight(reservationNumber, amount) {
  const payload = {
    reservationNumber,
    amount,
    createdAt: new Date().toISOString()
  };
  try {
    sessionStorage.setItem(INFLIGHT_KEY, JSON.stringify(payload));
    localStorage.setItem(LOCAL_PENDING_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function clearPayfastInflight() {
  clearPayfastStorage();
}
